import { CheckCircleIcon, ClockIcon, CircleIcon, GoalIcon, XCircleIcon } from "@primer/octicons-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { HttpError, rp } from "../api/client";
import { useIntent, useIntents } from "../api/hooks";
import type { CheckState, DocCheck, Intent, IntentState, IntentView } from "../api/types";
import { Empty, ErrorMessage, Loading, PageHead, Query, TimeAgo, Who } from "../components/common";
import { SpecReview, Steps, specChangeOf } from "../components/IntentWorkflow";
import { StateLabel } from "../components/StateLabel";
import { Badge, Counter, type Tone } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { List, ListItem } from "../components/ui/list";
import { cn } from "../lib/utils";

/** Board order: what is waiting first, what is done last, a regression always visible. */
const COLUMNS: IntentState[] = ["open", "in_progress", "proven", "regressed"];

export const INTENT_STATE: Record<IntentState, { label: string; tone: Tone; Icon: typeof CircleIcon }> = {
  open: { label: "Open", tone: "default", Icon: CircleIcon },
  in_progress: { label: "Building", tone: "yellow", Icon: ClockIcon },
  proven: { label: "Done", tone: "purple", Icon: CheckCircleIcon },
  regressed: { label: "Broken", tone: "red", Icon: XCircleIcon },
};

const STATE_TEXT: Record<IntentState, string> = { open: "", in_progress: "text-warning", proven: "text-merged", regressed: "text-danger" };

export function IntentStateLabel({ state }: { state: IntentState }) {
  const s = INTENT_STATE[state] ?? INTENT_STATE.open;
  return (
    <Badge tone={s.tone}>
      <s.Icon size={12} /> {s.label}
    </Badge>
  );
}

const CHECK_TONE: Record<CheckState, Tone> = { holds: "purple", broken: "red", pending: "yellow" };
const CHECK_LABEL: Record<CheckState, string> = { holds: "Pass", broken: "Fail", pending: "Not yet" };

/** Who acts next, as a small label: a person (yellow) or the agent. */
function Waiting({ on }: { on?: "person" | "agent" }) {
  if (on === "person") return <Badge tone="yellow">Needs you</Badge>;
  if (on === "agent") return <Badge>Agent</Badge>;
  return null;
}

/** What people asked for, as a board by state: open, in progress, proven, regressed. */
export function Intents() {
  const { repo = "" } = useParams();
  const q = useIntents(repo);
  return (
    <div>
      <PageHead title="Intents" />
      {q.error instanceof HttpError && q.error.status === 404 ? (
        <Empty>Upgrade bee-server for intents.</Empty>
      ) : (
        <Query q={q}>
          {(list) =>
            list.length === 0 ? (
              <Empty icon={GoalIcon}>
                No intents. Start one with <code>/intent</code>.
              </Empty>
            ) : (
              <div className="grid grid-cols-4 items-start gap-3 max-xl:grid-cols-2 max-sm:grid-cols-1">
                {COLUMNS.map((s) => (
                  <Column key={s} repo={repo} state={s} list={list.filter((i) => i.state === s)} />
                ))}
              </div>
            )
          }
        </Query>
      )}
    </div>
  );
}

function Column({ repo, state, list }: { repo: string; state: IntentState; list: Intent[] }) {
  const s = INTENT_STATE[state];
  return (
    <section className="min-w-0 rounded-md border border-border-muted bg-muted" aria-label={s.label}>
      <h2 className="flex items-center gap-2 px-3 py-2 text-sm font-semibold">
        <s.Icon size={16} className={STATE_TEXT[state]} />
        {s.label} <Counter>{list.length}</Counter>
      </h2>
      <div className="flex flex-col gap-2 px-2 pb-2">
        {list.map((i) => (
          <Link
            key={i.id}
            to={`${rp(repo)}/intents/${i.id}`}
            className={cn(
              "flex min-w-0 flex-col gap-2 rounded-md border bg-card p-3 text-foreground hover:bg-accent hover:no-underline",
              i.waiting_on === "person" && "border-warning-muted",
              state === "regressed" && "border-danger-muted",
            )}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs text-muted-foreground">{i.id}</span>
              <Waiting on={i.waiting_on} />
            </span>
            <span className="line-clamp-3 text-sm font-semibold">{i.words.trim()}</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
              <Who name={i.owner} size={16} /> · <TimeAgo ms={i.created} />
            </span>
          </Link>
        ))}
        {list.length === 0 && <p className="px-1 pb-1 text-xs text-faint">None</p>}
      </div>
    </section>
  );
}

/**
 * One intent, in the order work flows: the person's words, where it is, then its spec, the
 * changes that build it, and what they proved.
 */
export function IntentDetail() {
  const { repo = "", id = "" } = useParams();
  const q = useIntent(repo, id);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  const v = q.data as IntentView;
  const pending = specChangeOf(v);
  const agents = [...new Set(v.changes.map((c) => c.agent))];
  return (
    <div className="mt-6 flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="whitespace-pre-wrap text-2xl font-normal">{v.words.trim()}</h1>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <IntentStateLabel state={v.state} />
          <span className="font-mono">{v.id}</span>·
          <span>
            <b className="font-semibold text-foreground">{v.owner}</b>
            {v.author !== v.owner && <> via {v.author}</>}
          </span>
          · <TimeAgo ms={v.created} />
        </div>
      </header>

      <Steps v={v} />

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-6">
          <Section title="Spec">
            {pending ? (
              <SpecReview repo={repo} v={v} />
            ) : v.spec ? (
              <Card className="mt-0">
                <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <Link to={`${rp(repo)}/docs?view=docs&select=${encodeURIComponent(v.spec.doc)}`} className="font-mono text-sm">
                    {v.spec.doc}
                  </Link>
                  <Badge tone={v.spec.class === "stable" ? "green" : "default"}>{v.spec.status || v.spec.class}</Badge>
                  <span className="ml-auto text-sm text-muted-foreground">
                    {v.spec.checks.length} requirement{v.spec.checks.length === 1 ? "" : "s"}
                  </span>
                </div>
              </Card>
            ) : (
              <p className="m-0 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">Agent writes the spec.</p>
            )}
          </Section>

          <Section title="Changes" count={v.changes.length}>
            {v.changes.length === 0 ? (
              <p className="m-0 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">None.</p>
            ) : (
              <Card className="mt-0">
                <List>
                  {v.changes.map((c) => (
                    <ListItem key={c.id} className="flex-wrap">
                      <StateLabel state={c.state} />
                      {(c.docs?.length || (v.spec && c.paths?.includes(v.spec.doc))) && <Badge tone="yellow">Spec</Badge>}
                      <Link to={`${rp(repo)}/changes/${c.id}`} className="min-w-48 flex-1 font-semibold text-foreground">
                        {c.message.split("\n")[0]}
                      </Link>
                      <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                        <Who name={c.agent} size={16} agent /> · <TimeAgo ms={c.updated} />
                      </span>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Section>

          {!pending && v.outcomes.length > 0 && (
            <Section title="Outcomes" count={`${v.outcomes.filter((o) => o.state === "holds").length}/${v.outcomes.length}`}>
              <Card className="mt-0">
                <List>
                  {v.outcomes.map((o) => (
                    <Outcome key={o.id} repo={repo} o={o} draft={v.spec?.class !== "stable"} />
                  ))}
                </List>
              </Card>
            </Section>
          )}
        </div>

        <aside className="flex min-w-0 flex-[1_1_220px] flex-col text-sm">
          <Side title="Owner">{v.owner}</Side>
          {agents.length > 0 && <Side title="Agents">{agents.join(", ")}</Side>}
          {v.proven_at && (
            <Side title="Done">
              <TimeAgo ms={v.proven_at} />
            </Side>
          )}
        </aside>
      </div>
    </div>
  );
}

function Section({ title, count, children }: { title: string; count?: number | string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="m-0 flex items-center gap-2 text-base font-semibold">
        {title} {count !== undefined && count !== 0 && <Counter>{count}</Counter>}
      </h2>
      {children}
    </section>
  );
}

function Outcome({ repo, o, draft }: { repo: string; o: DocCheck; draft: boolean }) {
  const proof = o.proofs?.[0];
  return (
    <ListItem className="flex-wrap items-start">
      <Badge tone={draft ? "default" : CHECK_TONE[o.state]}>{draft ? "Draft" : CHECK_LABEL[o.state]}</Badge>
      <div className="min-w-48 flex-1">
        <Link to={`${rp(repo)}/docs?view=docs&select=${encodeURIComponent(o.doc)}`} className="font-semibold text-foreground">
          {o.title || o.id}
        </Link>
        {o.state !== "holds" && o.reason && <div className="text-xs text-muted-foreground">{o.reason}</div>}
      </div>
      {proof && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {proof.kind && <Badge tone="blue">{proof.kind}</Badge>}
          <span className="font-mono" title={`${proof.job}: ${proof.file ? `${proof.file}::` : ""}${proof.test}`}>
            {proof.test}
          </span>
          {o.proofs.length > 1 && ` +${o.proofs.length - 1}`}
        </span>
      )}
    </ListItem>
  );
}

function Side({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border-muted py-3 first:pt-0 last:border-b-0">
      <h3 className="m-0 text-xs font-semibold text-muted-foreground">{title}</h3>
      <span>{children}</span>
    </div>
  );
}
